class WHowto extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.viewport = this.querySelector('.w-howto__viewport');
    this.track = this.querySelector('.w-howto__track');
    this.dots = Array.from(this.querySelectorAll('.w-howto__dot'));
    this.cards = this.track ? Array.from(this.track.children) : [];
    this.count = this.cards.length;
    if (!this.viewport || !this.track || this.count <= 1) return;
    const { signal } = this.controller;
    this.mq = window.matchMedia('(max-width: 989px)');
    this.index = 0;
    this.animating = false;
    this.prevButton = this.querySelector('[data-direction="prev"]');
    this.nextButton = this.querySelector('[data-direction="next"]');
    this.prevButton?.addEventListener('click', () => this.move(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.move(1), { signal });
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.select(i), { signal }));
    this.track.addEventListener('transitionend', (event) => this.onTransitionEnd(event), {
      signal,
    });
    this.viewportWidth = window.innerWidth;
    window.addEventListener(
      'resize',
      () => {
        if (window.innerWidth === this.viewportWidth) return;
        this.viewportWidth = window.innerWidth;
        this.refresh();
      },
      { signal },
    );
    let touchStartX = null;
    let touchStartY = null;
    let touchCurrentX = null;
    let dragging = false;
    let directionLock = null;
    let dragStartOffset = 0;
    this.viewport.addEventListener(
      'touchstart',
      (event) => {
        if (!this.mq.matches || this.count <= 1) return;
        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
        touchCurrentX = touchStartX;
        dragging = true;
        directionLock = null;
        dragStartOffset = -this.index * this.step();
        this.track.style.transition = 'none';
      },
      { passive: true, signal },
    );
    this.viewport.addEventListener(
      'touchmove',
      (event) => {
        if (!dragging) return;
        touchCurrentX = event.touches[0].clientX;
        const currentY = event.touches[0].clientY;
        const dx = touchCurrentX - touchStartX;
        const dy = currentY - touchStartY;
        if (directionLock === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
          directionLock = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
        }
        if (directionLock === 'vertical') {
          dragging = false;
          this.track.style.transition = '';
          this.setPosition(true);
          return;
        }
        if (directionLock === 'horizontal') {
          event.preventDefault();
        }
        this.track.style.transform = 'translateX(' + (dragStartOffset + dx) + 'px)';
      },
      { passive: false, signal },
    );
    this.viewport.addEventListener(
      'touchend',
      () => {
        if (!dragging) return;
        dragging = false;
        const dx = touchCurrentX - touchStartX;
        touchStartX = null;
        touchStartY = null;
        touchCurrentX = null;
        directionLock = null;
        this.track.style.transition = '';
        const direction = dx < 0 ? 1 : -1;
        const targetIndex = Math.max(0, Math.min(this.index + direction, this.maxIndex()));
        const atEdge = Math.abs(dx) > 40 && targetIndex === this.index;
        if (Math.abs(dx) > 40 && targetIndex !== this.index) {
          this.move(direction);
        } else if (atEdge) {
          this.track.style.transition = 'transform 0.7s ease';
          this.track.style.transform = 'translateX(' + -this.index * this.step() + 'px)';
        } else {
          this.setPosition(true);
        }
      },
      { passive: true, signal },
    );
    this.viewport.addEventListener(
      'touchcancel',
      () => {
        if (!dragging) return;
        dragging = false;
        this.track.style.transition = '';
        this.setPosition(true);
        touchStartX = null;
        touchStartY = null;
        touchCurrentX = null;
        directionLock = null;
      },
      { passive: true, signal },
    );
    this.refresh();
  }
  disconnectedCallback() {
    this.controller?.abort();
  }
  step() {
    return (
      this.cards[0].getBoundingClientRect().width +
      (parseFloat(getComputedStyle(this.track).columnGap) || 0)
    );
  }
  visibleCount() {
    return Math.max(1, Math.floor((this.viewport.clientWidth + 2) / this.step()));
  }
  maxIndex() {
    return Math.max(0, this.count - this.visibleCount());
  }
  setPosition(animate) {
    this.track.style.transition = animate ? '' : 'none';
    this.track.style.transform = 'translateX(' + -this.index * this.step() + 'px)';
    if (!animate) {
      void this.track.offsetWidth;
      this.track.style.transition = '';
    }
  }
  updateUI() {
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
    if (this.prevButton) this.prevButton.disabled = this.index <= 0;
    if (this.nextButton) this.nextButton.disabled = this.index >= this.maxIndex();
  }
  refresh() {
    this.animating = false;
    if (this.animTimer) clearTimeout(this.animTimer);
    if (!this.mq.matches) {
      this.index = 0;
      this.track.style.transition = 'none';
      this.track.style.transform = '';
      void this.track.offsetWidth;
      this.track.style.transition = '';
      this.updateUI();
      return;
    }
    this.index = Math.min(this.index, this.maxIndex());
    this.setPosition(false);
    this.updateUI();
  }
  move(direction) {
    if (!this.mq.matches) return;
    this.animateTo(Math.max(0, Math.min(this.index + direction, this.maxIndex())));
  }
  select(index) {
    if (!this.mq.matches) return;
    this.animateTo(Math.max(0, Math.min(index, this.maxIndex())));
  }
  animateTo(index) {
    if (index === this.index) return;
    this.animating = true;
    this.index = index;
    this.setPosition(true);
    this.updateUI();
    if (this.animTimer) clearTimeout(this.animTimer);
    this.animTimer = setTimeout(() => {
      this.animating = false;
    }, 500);
  }
  onTransitionEnd(event) {
    if (event.target !== this.track || event.propertyName !== 'transform') return;
    this.animating = false;
  }
}
customElements.define('w-howto', WHowto);